import type {
  CoCustomerAggregated,
  CoCustomerTimeSeriesPoint,
} from 'src/services/coCustomerAnalyticsService';
import { AnalyticsCard } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCard/AnalyticsCard';
import { AnalyticsCustomerPopup } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerPopup/AnalyticsCustomerPopup';
import { EscalationTrendsCard } from 'src/pages/AnalyticsPage/AnalyticsCustomer/EscalationTrendsCard/EscalationTrendsCard';
import { EscalationTrendsLineChart } from 'src/pages/AnalyticsPage/AnalyticsCustomer/EscalationTrendsCard/EscalationTrendsLineChart';
import { DateItemsGroupByX } from 'src/pages/AnalyticsPage/AnalyticsCustomer/DateItems';
import { cardMetric, cardTitle, formatNum, trendOf } from '../utils/metricFormatters';
import { DateRangeItem } from '../AnalyticsCoCustomer.types';

const ESCALATION_CARDS_CONFIG = [
  {
    key: 'escalatedKnowledgeGap',
    color: '#E52020',
    title: 'Knowledge gap',
    tooltip: (
      <span>
        Tickets escalated because the AI lacked sufficient information to provide a reliable answer
      </span>
    ),
  },
  {
    key: 'escalatedModelConfiguration',
    color: '#F9AE00',
    title: 'Workflow rule',
    tooltip: <span>Tickets escalated automatically based on configured Workflows</span>,
  },
  {
    key: 'escalatedUserRequest',
    color: '#54DAE2',
    title: 'Customer request',
    tooltip: (
      <span>
        Tickets escalated because the customer explicitly asked to speak with a human agent
      </span>
    ),
  },
  {
    key: 'escalatedOther',
    color: '#DAE0E2',
    title: 'Other escalations',
    tooltip: (
      <span>
        Tickets escalated for reasons that don’t fall into the defined escalation categories
      </span>
    ),
  },
] as const;

type EscalationsOverviewSectionProps = {
  dateRange: DateRangeItem;
  aggregatedData: CoCustomerAggregated | undefined;
  timeSeriesResponse: CoCustomerTimeSeriesPoint[];
};

export const EscalationsOverviewSection = ({
  dateRange,
  aggregatedData,
  timeSeriesResponse,
}: EscalationsOverviewSectionProps) => (
  <>
    <div className={'flex flex-row flex-wrap md:gap-[10px] gap-[6px]'}>
      {ESCALATION_CARDS_CONFIG.map(({ key, color, title, tooltip }) => (
        <div className={'flex flex-1 min-w-[220px]'} key={key}>
          <AnalyticsCard
            color={color}
            title={cardTitle(title)}
            metric={cardMetric(formatNum(aggregatedData?.[key]?.value || 0))}
            trend={trendOf(aggregatedData?.[key]?.percentage)}
            toolTipDescription={tooltip}
          />
        </div>
      ))}
    </div>
    <div className={'flex flex-row flex-wrap md:gap-[10px] gap-[6px]'}>
      <div className={'flex flex-[32%] min-w-[280px]'}>
        <EscalationTrendsCard
          otherEscalations={aggregatedData?.escalatedOther?.value || 0}
          customerRequest={aggregatedData?.escalatedUserRequest?.value || 0}
          knowledgeGap={aggregatedData?.escalatedKnowledgeGap?.value || 0}
          workflowRule={aggregatedData?.escalatedModelConfiguration?.value || 0}
        />
      </div>
      <div className={'flex flex-[66%] min-w-[320px]'}>
        <EscalationTrendsLineChart
          title={'Escalation trends in dynamic'}
          showLegend={false}
          data={timeSeriesResponse}
          icon={
            <AnalyticsCustomerPopup
              title={'Escalation trends by type'}
              dateRange={dateRange}
              chartType={'escalationTrends'}
              className={''}
              xAxisSelect={DateItemsGroupByX}
              chartHeight={500}
              cartesianGridVisible={true}
            />
          }
        />
      </div>
    </div>
  </>
);
